'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WaitlistPopupProps {
  config: {
    isActive: boolean;
    headline: string;
    description: string;
    pricingPitch: string;
    ctaText: string;
    directEmailText?: string;
  };
}

export default function VanguardWaitlistPopup({ config }: WaitlistPopupProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Only show if active in Sanity
    if (!config.isActive) return;

    // Optional: Only show once per session using sessionStorage
    const hasSeen = sessionStorage.getItem('vanguard_waitlist_seen');
    if (!hasSeen) {
      // Delay popup by 2 seconds for dramatic effect
      const timer = setTimeout(() => setIsOpen(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [config.isActive]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('vanguard_waitlist_seen', 'true');
  };

  const scrollToContact = () => {
    handleClose();
    // Smooth scroll to contact form instead of bottom of page
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          {/* Backdrop Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
          />

          {/* Popup Container */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-zinc-950 border border-white/10 shadow-[0_0_80px_rgba(255,0,0,0.15)] overflow-hidden"
            style={{ clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' }}
          >
            {/* Top decorative line */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand via-brand/50 to-transparent" />
            
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors z-20"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-8 relative z-10">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-brand animate-[ping_1.5s_infinite]" />
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand">Closed Beta Notice</span>
              </div>

              <h2 className="text-2xl font-serif font-black text-white mb-4 leading-tight">
                {config.headline}
              </h2>

              <div className="space-y-4 mb-8">
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {config.description}
                </p>
                <div className="p-4 bg-brand/5 border border-brand/20 relative">
                  {/* Decorative corner */}
                  <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-brand" />
                  <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-brand" />
                  <p className="text-zinc-300 text-xs font-mono leading-relaxed">
                    {config.pricingPitch}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <button
                  onClick={scrollToContact}
                  className="flex-1 bg-brand text-white py-3 px-6 text-xs font-bold uppercase tracking-widest hover:bg-brand-light transition-colors relative group"
                >
                  <span className="relative z-10">{config.ctaText}</span>
                  <div className="absolute inset-0 bg-white/20 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                </button>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 border border-white/10 text-zinc-400 text-xs font-bold uppercase tracking-widest hover:text-white hover:border-white/30 transition-colors"
                >
                  Dismiss
                </button>
              </div>

              {config.directEmailText && (
                <div className="text-center">
                  <span className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 transition-colors cursor-text">
                    {config.directEmailText}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
